import * as Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import React, { useState } from 'react';
import { useParams } from 'react-router-dom';

import SkeletonBlock from '@components/common/Skeleton/SkeletonBlock';

import useTranslation from '@hooks/useTranslation';

import { useGetActionItemsStatusesOptions } from '@pages/ExecutiveSummary/hooks/useGetActionItemsStatusesOptions';

import { selectAppLocationSelectValue } from '@store/useAppStore';

import { getAiByStatusSeriesData } from '../../helpers/getAiByStatusSeriesData';
import type { IActionItemsStatusesVisible } from '../../model/actionItemsStatuses';
import { useAiStatusesQuery } from '../../services/useAiStatusesQuery';
import NoData from '../NoData/NoData';
import StatisticWrapper from '../StatisticWrapper/StatisticWrapper';
import ActionItemsLegendTooltip from './ActionItemsLegendTooltip';
import style from './style.module.scss';

const ActionItemsStatuses = () => {
  const [aiStatusesVisible, setAiStatusesVisible] = useState<IActionItemsStatusesVisible>({
    'Need response': true,
    'Pending Update': true,
  });
  const { customerId } = useParams();
  const { t } = useTranslation();
  const locationSelectValue = selectAppLocationSelectValue();

  const { data, isPlaceholderData } = useAiStatusesQuery({
    locationIds:
      locationSelectValue.level_name === 'allFacilities' ? undefined : locationSelectValue?.filterIds,
  });

  const { needResponseTotal, pendingUpdatesTotal, needResponse, pendingUpdates } = data!;

  const seriesData = getAiByStatusSeriesData(needResponse, pendingUpdates, aiStatusesVisible);

  const { aiByStatusOptions, aiByTypeOptions } = useGetActionItemsStatusesOptions(
    needResponseTotal,
    pendingUpdatesTotal,
    needResponse,
    pendingUpdates,
    seriesData,
    setAiStatusesVisible,
  );

  const hasTypeChart = needResponseTotal || pendingUpdatesTotal > 0;
  const hasStatusChart = seriesData.length > 0;
  const hasChartData = !isPlaceholderData && !!hasTypeChart && !!hasStatusChart;

  return (
    <StatisticWrapper
      title={t('Action Items Statuses')}
      link={`/customer/${customerId}`}
      linkText="View Action Items"
    >
      <>
        {!hasChartData && <NoData type="autoHeight" />}
        {isPlaceholderData && (
          <div className={style.fullHeightSkeleton}>
            <SkeletonBlock className={style.skeletonBar} />
            <SkeletonBlock className={style.skeletonLegend} />
            <SkeletonBlock className={style.skeletonCircle} />
          </div>
        )}
        {hasChartData && (
          <>
            <HighchartsReact
              highcharts={Highcharts}
              options={aiByTypeOptions}
              containerProps={{ className: 'printHighchartsChart' }}
            />
            <HighchartsReact
              highcharts={Highcharts}
              options={aiByStatusOptions}
              containerProps={{ className: 'printHighchartsChart' }}
            />
            <ActionItemsLegendTooltip id="action-items-legend-tooltip" />
          </>
        )}
      </>
    </StatisticWrapper>
  );
};

export default ActionItemsStatuses;
